import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, BookOpen, Check, Clock, GraduationCap, Layers3, Loader2, User2, BadgeCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { RegisterCourseButton } from "../components/RegisterCourseButton";
import { Reveal } from "../components/Reveal";

const inputClass =
  "h-11 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-muted-foreground/60 focus:border-gold focus:ring-2 focus:ring-gold/25";

export default function CourseDetailPage() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [enrolling, setEnrolling] = useState(false);
  const [enrolled, setEnrolled] = useState(false);

  useEffect(() => {
    setCourse(null);
    setNotFound(false);
    api
      .get(`/quranic-courses/${courseId}`)
      .then((r) => setCourse(r.data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        else toast.error(formatApiError(e));
      });
  }, [courseId]);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
      api.get("/quranic-courses/my/enrollments").then((r) => {
        if (r.data.some((e) => e.course_id === courseId)) setEnrolled(true);
      }).catch(() => {});
    }
  }, [user, courseId]);

  const enroll = async () => {
    if (name.trim().length < 2) return toast.error("Please enter your name");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("Please enter a valid email");
    setEnrolling(true);
    try {
      await api.post(`/quranic-courses/${courseId}/enroll`, { name, email });
      setEnrolled(true);
      toast.success("Enrolled — check your inbox");
    } catch (e) {
      if (e?.response?.status === 409) {
        setEnrolled(true);
        toast.info("You're already enrolled in this course");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setEnrolling(false);
    }
  };

  if (notFound) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bone px-4 pt-[72px]" data-testid="course-not-found">
        <div className="text-center">
          <BookOpen className="mx-auto h-10 w-10 text-forest/25" />
          <h1 className="mt-4 font-display text-2xl font-extrabold text-forest-deep">Course not found</h1>
          <Link to="/quranic-studies" data-testid="back-to-catalog"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-forest px-7 py-3 font-display text-sm font-bold text-bone">
            <ArrowLeft className="h-4 w-4" /> Back to catalog
          </Link>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bone pt-[72px]">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone pt-[72px]" data-testid="course-detail-page">
      <div className="relative overflow-hidden bg-forest-deep">
        <div className="bg-noise pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <Reveal>
            <nav className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-100/50" data-testid="breadcrumb">
              <Link to="/" className="transition-colors hover:text-bone">Home</Link>
              <span>/</span>
              <Link to="/quranic-studies" className="transition-colors hover:text-bone">Quranic Studies</Link>
              <span>/</span>
              <span className="text-gold">{course.title}</span>
            </nav>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <span className="rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                {course.category}
              </span>
              <span className="rounded-full border border-emerald-100/20 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-100/70">
                {course.level}
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.14}>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold tracking-tight text-bone sm:text-5xl">
              {course.title}
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-emerald-100/70 sm:text-lg">
              {course.description}
            </p>
            <p className="mt-4 text-sm text-emerald-100/70" data-testid="course-tutor">
              Tutor:{" "}
              <Link to="/tutors" className="font-semibold text-gold transition-colors hover:text-bone">{course.instructor}</Link>
            </p>
          </Reveal>
          <Reveal delay={0.26}>
            <div className="mt-7 flex flex-wrap gap-6 text-sm text-emerald-100/70">
              <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-gold" />{course.duration}</span>
              <span className="flex items-center gap-2"><Layers3 className="h-4 w-4 text-gold" />{course.lessons} lessons</span>
              <span className="flex items-center gap-2"><User2 className="h-4 w-4 text-gold" />{course.instructor}</span>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="space-y-12 lg:col-span-8">
            <Reveal>
              <section data-testid="course-outcomes">
                <h2 className="font-display text-2xl font-bold text-forest-deep">
                  What you'll <em className="font-editorial font-medium italic text-gold-dark">walk away with</em>
                </h2>
                <ul className="mt-6 space-y-3.5">
                  {course.outcomes.map((o, i) => (
                    <li key={i} className="flex items-start gap-3.5 rounded-2xl border border-border bg-white p-5">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-forest text-bone">
                        <Check className="h-3.5 w-3.5" />
                      </span>
                      <span className="text-sm leading-relaxed text-foreground/80">{o}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </Reveal>

            <Reveal>
              <section data-testid="course-curriculum">
                <h2 className="font-display text-2xl font-bold text-forest-deep">Curriculum</h2>
                <div className="mt-6 space-y-3">
                  {course.curriculum.map((m, i) => (
                    <div key={i} className="flex items-center gap-4 rounded-2xl border border-border bg-white px-5 py-4 transition-colors hover:border-gold/40">
                      <span className="font-mono text-sm font-semibold text-gold-dark">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-sm font-medium text-forest-deep">{m}</span>
                    </div>
                  ))}
                </div>
              </section>
            </Reveal>

            <Reveal>
              <section className="rounded-3xl border border-border bg-white p-7" data-testid="course-instructor">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-dark">Your instructor</p>
                <div className="mt-4 flex items-center gap-4">
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-forest font-display text-xl font-extrabold text-bone">
                    {course.instructor.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </span>
                  <div>
                    <p className="font-display text-lg font-bold text-forest-deep">{course.instructor}</p>
                    <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <GraduationCap className="h-3.5 w-3.5 text-gold-dark" /> Qualified instructor, LearnYourWay faculty
                    </p>
                  </div>
                </div>
              </section>
            </Reveal>
          </div>

          <div className="lg:col-span-4">
            <Reveal delay={0.1}>
              <div className="rounded-[2rem] border border-border bg-white p-7 shadow-[0_25px_70px_rgba(10,41,30,0.09)] lg:sticky lg:top-24" data-testid="enroll-card">
                {enrolled ? (
                  <div className="text-center" data-testid="enroll-success">
                    <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold shadow-[0_0_35px_rgba(212,175,55,0.45)]">
                      <BadgeCheck className="h-7 w-7 text-forest-deep" />
                    </span>
                    <h3 className="mt-5 font-display text-xl font-bold text-forest-deep">You're enrolled</h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      A confirmation email is on its way. Your instructor will reach out with orientation
                      details and your first session schedule.
                    </p>
                    <Link to="/quranic-studies" data-testid="enroll-back-catalog"
                      className="mt-6 inline-block rounded-full border border-border px-6 py-3 text-sm font-semibold text-foreground/70 transition-colors hover:text-forest">
                      Browse more courses
                    </Link>
                  </div>
                ) : (
                  <>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-dark">Enrollment</p>
                    <p className="mt-3 font-display text-xl font-bold text-forest-deep">Enrol in this course</p>
                    {course.price > 0 && (
                      <p className="mt-3 text-sm text-muted-foreground" data-testid="course-detail-price">
                        <span className="font-display text-xl font-extrabold text-forest-deep">${course.price}</span> /lesson
                      </p>
                    )}
                    <div className="mt-6 space-y-3">
                      {!user && (
                        <>
                          <input data-testid="enroll-name-input" className={inputClass} value={name}
                            onChange={(e) => setName(e.target.value)} placeholder="Full name" autoComplete="name" />
                          <input data-testid="enroll-email-input" type="email" className={inputClass} value={email}
                            onChange={(e) => setEmail(e.target.value)} placeholder="Email address" autoComplete="email" />
                        </>
                      )}
                      <button onClick={enroll} data-testid="enroll-submit-btn" disabled={enrolling}
                        className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 font-display text-sm font-bold text-forest-deep transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(212,175,55,0.4)] disabled:opacity-60">
                        {enrolling && <Loader2 className="h-4 w-4 animate-spin" />}
                        Enroll now
                      </button>
                      <RegisterCourseButton course={course} courseType="quranic" testId="course-detail-register-btn" label="Register & pay" className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border-2 border-forest py-3.5 font-display text-sm font-bold text-forest transition-transform duration-200 hover:-translate-y-0.5" />
                    </div>
                    <p className="mt-4 text-center text-xs leading-relaxed text-muted-foreground">
                      {user
                        ? `Enrolling as ${user.email}`
                        : "Small live cohorts · certificate of completion included"}
                    </p>
                  </>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}
